import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, Plus, Calendar as CalendarIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const AllBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [icalUrl, setIcalUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, cancelled
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: '',
    check_out: '',
    num_guests: 1,
    room_id: '',
    total_amount: '',
    advance_amount: '',
    special_requests: '',
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [manualRes, publicRes, roomsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/admin/public-bookings`),
        axios.get(`${API}/public/rooms`)
      ]);

      // Combine all bookings with a unified structure
      const allBookings = [
        ...manualRes.data.bookings.map(b => ({ 
          ...b, 
          source: 'manual', 
          booking_status: b.status || 'confirmed',
          payment_status: 'paid' // Manual bookings are considered paid
        })),
        ...publicRes.data.bookings.map(b => ({ ...b, source: 'online' }))
      ];

      // Sort by created_at (newest first), then by check-in date
      allBookings.sort((a, b) => {
        const dateA = new Date(a.created_at || a.check_in);
        const dateB = new Date(b.created_at || b.check_in);
        return dateB - dateA;
      });
      
      setBookings(allBookings);
      setRooms(roomsRes.data.rooms || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available rooms when dates change
  const fetchAvailableRooms = async (checkIn, checkOut) => {
    if (!checkIn || !checkOut) {
      setAvailableRooms(rooms);
      return;
    }
    
    setCheckingAvailability(true);
    try {
      const response = await axios.get(`${API}/public/available-rooms?check_in=${checkIn}&check_out=${checkOut}`);
      if (response.data.success) {
        setAvailableRooms(response.data.available_rooms);
        // Reset room selection if current room is not available
        if (formData.room_id && !response.data.available_rooms.find(r => r.id === formData.room_id)) {
          setFormData(prev => ({ ...prev, room_id: '' }));
        }
      }
    } catch (error) {
      console.error('Error fetching available rooms:', error);
      setAvailableRooms(rooms);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Update available rooms when dates change
  useEffect(() => {
    if (formData.check_in && formData.check_out && addDialogOpen) {
      fetchAvailableRooms(formData.check_in, formData.check_out);
    }
  }, [formData.check_in, formData.check_out, addDialogOpen]);

  // Calculate advance amount (50%) when total changes
  useEffect(() => {
    if (formData.total_amount) {
      const advance = Math.round(parseFloat(formData.total_amount) * 0.5);
      setFormData(prev => ({ ...prev, advance_amount: advance.toString() }));
    }
  }, [formData.total_amount]);

  const handleConfirmPayment = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/public-bookings/${bookingId}/confirm-payment`);
      toast.success('Payment confirmed! Booking is now active.');
      fetchAllData();
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error('Failed to confirm payment');
    }
  };

  const handleCancelBooking = async (booking) => {
    if (!window.confirm(`Delete booking for ${booking.guest_name}?`)) return;

    try {
      if (booking.source === 'online') {
        await axios.delete(`${API}/admin/public-bookings/${booking.id}`);
      } else {
        await axios.delete(`${API}/bookings/${booking.id}`);
      }
      toast.success('Booking deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking');
    }
  };

  const handleAddManualBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.check_in || !formData.check_out) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    if (!formData.room_id) {
      toast.error('Please select a room');
      return;
    }

    try {
      // Find room name if room_id is selected
      const selectedRoom = rooms.find(r => r.id === formData.room_id);
      
      const bookingData = {
        guest_name: formData.guest_name,
        guest_email: formData.guest_email || null,
        guest_phone: formData.guest_phone,
        check_in: formData.check_in,
        check_out: formData.check_out,
        num_guests: parseInt(formData.num_guests),
        room_type: selectedRoom ? selectedRoom.name : '',
        total_amount: formData.total_amount ? parseFloat(formData.total_amount) : null,
        booking_source: 'manual',
        status: 'confirmed',
        special_requests: formData.special_requests || null,
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking created successfully!');
      setAddDialogOpen(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking');
    }
  };

  const handleImportCalendar = async () => {
    if (!icalUrl.trim()) {
      toast.error('Please enter a valid iCal URL');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/bookings/import-calendar`, {
        ical_url: icalUrl,
        booking_source: 'airbnb'
      });

      if (response.data.success) {
        toast.success(`Imported ${response.data.imported} bookings! (${response.data.skipped} skipped as duplicates)`);
        setImportDialogOpen(false);
        setIcalUrl('');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error importing calendar:', error);
      toast.error(error.response?.data?.detail || 'Failed to import calendar');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      check_in: '',
      check_out: '',
      num_guests: 1,
      room_id: '',
      total_amount: '',
      advance_amount: '',
      special_requests: '',
    });
    setAvailableRooms(rooms);
  };

  const getStatusBadge = (booking) => {
    if (booking.booking_status === 'cancelled') {
      return <Badge className="bg-red-600">Cancelled</Badge>;
    }
    
    if (booking.source === 'manual' || booking.payment_status === 'paid' || booking.booking_status === 'confirmed') {
      return <Badge className="bg-green-600">Confirmed</Badge>;
    }
    
    return <Badge className="bg-yellow-600">Payment Pending</Badge>;
  };

  const getFilteredBookings = () => {
    if (filter === 'all') return bookings;
    if (filter === 'pending') {
      return bookings.filter(b => 
        b.source === 'online' && 
        b.payment_status === 'pending' && 
        b.booking_status === 'pending'
      );
    }
    if (filter === 'confirmed') {
      return bookings.filter(b => 
        b.source === 'manual' || 
        b.payment_status === 'paid' || 
        b.booking_status === 'confirmed'
      );
    }
    if (filter === 'cancelled') {
      return bookings.filter(b => b.booking_status === 'cancelled');
    }
    return bookings;
  };

  const filteredBookings = getFilteredBookings();
  const pendingCount = bookings.filter(b => 
    b.source === 'online' && b.payment_status === 'pending' && b.booking_status === 'pending'
  ).length;

  if (loading) {
    return <div className="text-center py-12">Loading bookings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="all-bookings-container">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">All Bookings</h1>
          <p className="text-gray-600 mt-1">Manage all bookings - manual and online</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Import Calendar */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-calendar-button">
                <CalendarIcon className="mr-2" size={16} />
                Import Airbnb
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Import Airbnb Calendar</DialogTitle>
                <DialogDescription>
                  Paste your Airbnb iCal calendar URL to import bookings
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ical_url">iCal Calendar URL</Label>
                  <Input
                    id="ical_url"
                    placeholder="https://www.airbnb.com/calendar/ical/..."
                    value={icalUrl}
                    onChange={(e) => setIcalUrl(e.target.value)}
                    data-testid="ical-url-input"
                  />
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                  <p className="font-semibold mb-2">How to get your Airbnb iCal URL:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to Airbnb Calendar</li>
                    <li>Click on "Availability settings"</li>
                    <li>Scroll to "Export calendar"</li>
                    <li>Copy the iCal link</li>
                  </ol>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleImportCalendar} 
                    disabled={importing}
                    data-testid="submit-import-button"
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Manual Booking */}
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="add-booking-button">
                <Plus className="mr-2" size={16} />
                Add Booking
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Manual Booking</DialogTitle>
                <DialogDescription>Add a new guest booking manually</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddManualBooking} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="guest_name">Guest Name *</Label>
                    <Input
                      id="guest_name"
                      value={formData.guest_name}
                      onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                      required
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guest_phone">Phone Number *</Label>
                    <Input
                      id="guest_phone"
                      value={formData.guest_phone}
                      onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                      required
                      data-testid="guest-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="guest_email">Email</Label>
                  <Input
                    id="guest_email"
                    type="email"
                    value={formData.guest_email}
                    onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                    data-testid="guest-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="check_in">Check-in Date *</Label>
                    <Input
                      id="check_in"
                      type="date"
                      value={formData.check_in}
                      onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                      required
                      data-testid="check-in-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="check_out">Check-out Date *</Label>
                    <Input
                      id="check_out"
                      type="date"
                      value={formData.check_out}
                      onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                      required
                      data-testid="check-out-input"
                    />
                  </div>
                </div>

                {/* Room Selection Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="room_id">Select Room * {checkingAvailability && <span className="text-blue-600 text-sm">(checking availability...)</span>}</Label>
                  <Select 
                    value={formData.room_id} 
                    onValueChange={(value) => setFormData({ ...formData, room_id: value })}
                    disabled={!formData.check_in || !formData.check_out}
                  >
                    <SelectTrigger data-testid="room-select">
                      <SelectValue placeholder={!formData.check_in || !formData.check_out ? "Select dates first" : "Choose a room"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRooms.length > 0 ? (
                        availableRooms.map((room) => (
                          <SelectItem key={room.id} value={room.id}>
                            {room.name} (Max {room.max_guests} guests) - ₹{room.base_price}/night
                          </SelectItem>
                        ))
                      ) : (
                        <div className="px-2 py-3 text-sm text-red-600">
                          No rooms available for selected dates
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {formData.check_in && formData.check_out && availableRooms.length === 0 && !checkingAvailability && (
                    <p className="text-sm text-red-600">All rooms are booked for these dates. Try different dates.</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="num_guests">Number of Guests *</Label>
                    <Input
                      id="num_guests"
                      type="number"
                      min="1"
                      value={formData.num_guests}
                      onChange={(e) => setFormData({ ...formData, num_guests: e.target.value })}
                      required
                      data-testid="num-guests-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_amount">Total Amount (₹)</Label>
                    <Input
                      id="total_amount"
                      type="number"
                      step="0.01"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      data-testid="total-amount-input"
                    />
                  </div>
                </div>

                {/* Advance Payment - Editable */}
                {formData.total_amount && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">Payment Breakdown</h4>
                    <div className="grid grid-cols-3 gap-4 items-end">
                      <div>
                        <p className="text-gray-600 text-sm mb-1">Total Amount</p>
                        <p className="font-bold text-lg">₹{parseFloat(formData.total_amount).toLocaleString()}</p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="advance_amount" className="text-gray-600 text-sm">Advance Received (₹)</Label>
                        <Input
                          id="advance_amount"
                          type="number"
                          min="0"
                          max={formData.total_amount}
                          value={formData.advance_amount}
                          onChange={(e) => setFormData({ ...formData, advance_amount: e.target.value })}
                          className="bg-white border-green-300 font-bold text-green-700"
                          data-testid="advance-amount-input"
                        />
                      </div>
                      <div>
                        <p className="text-gray-600 text-sm mb-1">Balance at Check-in</p>
                        <p className="font-bold text-lg text-orange-600" data-testid="balance-amount">
                          ₹{(parseFloat(formData.total_amount) - parseFloat(formData.advance_amount || 0)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="special_requests">Special Requests</Label>
                  <Textarea
                    id="special_requests"
                    value={formData.special_requests}
                    onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                    rows={3}
                    data-testid="special-requests-input"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="submit-booking-button">
                    Create Booking
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500 text-white flex items-center justify-center font-bold">
                {pendingCount}
              </div>
              <div>
                <p className="font-semibold text-yellow-800">Pending Payment Confirmation</p>
                <p className="text-sm text-yellow-700">
                  {pendingCount} booking{pendingCount > 1 ? 's' : ''} waiting for payment verification
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'confirmed', 'cancelled'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && pendingCount > 0 && (
              <span className="ml-1 bg-yellow-500 text-white text-xs px-1.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Bookings List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <CalendarIcon className="mx-auto mb-4" size={48} />
              <p>No bookings found</p>
            </CardContent>
          </Card>
        ) : (
          filteredBookings.map((booking) => {
            const isPending = booking.source === 'online' && 
                            booking.payment_status === 'pending' && 
                            booking.booking_status === 'pending';
            const isCancelled = booking.booking_status === 'cancelled';

            return (
              <Card 
                key={booking.id} 
                className={`${isPending ? 'border-yellow-300 bg-yellow-50' : isCancelled ? 'border-red-200 bg-red-50 opacity-75' : 'border-green-200'}`}
                data-testid={`booking-${booking.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{booking.guest_name}</h3>
                        {getStatusBadge(booking)}
                        <Badge variant="outline" className="text-xs">
                          {booking.source === 'online' ? 'Online Booking' : 
                           booking.booking_source === 'airbnb' ? 'Airbnb' : 'Manual Entry'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <span className="text-gray-600">Phone:</span>
                          <span className="ml-2 font-medium">{booking.guest_phone}</span>
                        </div>
                        {booking.guest_email && (
                          <div>
                            <span className="text-gray-600">Email:</span>
                            <span className="ml-2 font-medium truncate">{booking.guest_email}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Check-in:</span>
                          <span className="ml-2 font-medium">{booking.check_in}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Check-out:</span>
                          <span className="ml-2 font-medium">{booking.check_out}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Room:</span>
                          <span className="ml-2 font-medium">{booking.room_name || booking.room_type || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Guests:</span>
                          <span className="ml-2 font-medium">{booking.num_guests}</span>
                        </div>
                        {booking.total_amount && (
                          <div>
                            <span className="text-gray-600">Amount:</span>
                            <span className="ml-2 font-medium text-green-700">₹{booking.total_amount}</span>
                          </div>
                        )}
                        {booking.meal_plan && (
                          <div>
                            <span className="text-gray-600">Meal Plan:</span>
                            <span className="ml-2 font-medium">
                              {booking.meal_plan === 'room_only' ? 'Room Only' : 'With Meals'}
                            </span>
                          </div>
                        )}
                      </div>

                      {booking.special_requests && (
                        <div className="mt-2 text-sm">
                          <span className="text-gray-600">Special Requests:</span>
                          <span className="ml-2">{booking.special_requests}</span>
                        </div>
                      )}
                    </div>

                    {!isCancelled && (
                      <div className="flex gap-2 flex-shrink-0">
                        {isPending && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setConfirmDialogOpen(true);
                            }}
                            data-testid={`confirm-${booking.id}`}
                          >
                            <CheckCircle size={16} className="mr-1" />
                            Confirm
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancelBooking(booking)}
                          data-testid={`cancel-${booking.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Confirm Payment Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment Received</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <p>Confirm that you have received payment for:</p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div><strong>Guest:</strong> {selectedBooking.guest_name}</div>
                <div><strong>Phone:</strong> {selectedBooking.guest_phone}</div>
                <div><strong>Total Amount:</strong> ₹{selectedBooking.total_amount}</div>
                <div className="bg-green-100 p-2 rounded border border-green-300">
                  <strong className="text-green-800">Advance Payment (50%):</strong> 
                  <span className="text-green-800 font-bold text-lg ml-2">₹{Math.round(selectedBooking.total_amount * 0.5)}</span>
                </div>
                <div className="text-sm text-gray-600">Balance at check-in: ₹{Math.round(selectedBooking.total_amount * 0.5)}</div>
                <div><strong>Check-in:</strong> {selectedBooking.check_in}</div>
                <div><strong>Check-out:</strong> {selectedBooking.check_out}</div>
                <div><strong>Room:</strong> {selectedBooking.room_name}</div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm">
                <p className="text-yellow-800 font-medium mb-2">
                  By confirming, this booking will:
                </p>
                <ul className="list-disc list-inside text-yellow-700 space-y-1">
                  <li>Show as "Confirmed" in bookings list</li>
                  <li>Appear in the booking calendar</li>
                  <li>Block these dates for other bookings</li>
                </ul>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleConfirmPayment(selectedBooking.id)}
                  data-testid="confirm-payment-submit"
                >
                  Yes, Payment Received
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllBookings;
